import { useDispatch } from "react-redux";

import { FORM_RECEIVERS } from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/constants";

import __ from "web/Layout/Translations";
import { getLanguageCode } from "web/Layout/Translations/LanguageWrapper";

import EmailSenderActivityItem from "web/Components/Common/SubscriptionActivities/ActivityItem/EmailSenderItem/emailSenderItem";
import FileDownloaderActivityItem from "web/Components/Common/SubscriptionActivities/ActivityItem/FileDownloaderItem";
import FormFillerItem from "web/Components/Common/SubscriptionActivities/ActivityItem/FormFillerItem";
import ActivityItem from "web/Components/Common/SubscriptionActivities/ActivityItem/activityItem";

import { IApiRequestMethods } from "web/api/apiRequestTypes";
import downloadFile from "web/api/downloadFile";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import getAttachmentFiles from "web/utils/page/product/subscription/getAttachmentFiles";
import getBenefitAttachments from "web/utils/page/product/subscription/getBenefitAttachments";
import { getIdFromIri } from "web/utils/page/product/subscription/iri";
import setBenefitGroupTypeParam from "web/utils/page/product/subscription/setBenefitGroupTypeParam";
import BrowserPersistence from "web/utils/system/storage/storage/browserPersistence";

import { NOT_ACTIVE_STATUSES } from "web/constants/benefits";
import restUrls, { RestAPIS } from "web/constants/restUrls";
import storageNames from "web/constants/storageNames";

import {
  ISubscriptionItemCurrentUserSubscription,
  ISubscriptionUserCurrent,
} from "web/types/Subscription";

import { request } from "web/api";
import { setNotificationError } from "web/features/app/appSlice";

const storage = new BrowserPersistence();

export default (
  data: ISubscriptionUserCurrent,
  disablePending = true,
  setData: React.Dispatch<React.SetStateAction<ISubscriptionUserCurrent>>
) => {
  const dispatch = useDispatch();

  const elements = [];
  const benefitsWithFormsToUpdate: ISubscriptionItemCurrentUserSubscription[] =
    [];
  const languageCode = getLanguageCode();
  const {
    id: subscriptionId,
    items: benefits,
    agreementsPdfUrl,
    totals,
  } = data;

  const isAnyBenefitPending = benefits.some((ele) =>
    NOT_ACTIVE_STATUSES.includes(ele.status)
  );
  const showOfflineDeclarations =
    isAnyBenefitPending && agreementsPdfUrl && !disablePending;

  const onGeneratePDFHandler = async (url: string) => {
    const finalPdfGenerationUrl = `${RestAPIS.SUB_MANAGEMENT_API}${url}`;

    const token = storage.getItem(storageNames.tokenAccess);

    try {
      const response = await request(finalPdfGenerationUrl, {
        method: IApiRequestMethods.PUT,
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      const { url: pdfUrl } = response as { url: string };

      if (pdfUrl) window.open(pdfUrl);
    } catch (err: any) {
      if (err && err.response.status === 500) {
        const errorMessage = __(
          "Nie można wygenerować dokumentu ponieważ konfiguracja na to nie pozwala. Skontaktuj się ze swoim pracodawcą."
        );
        dispatch(
          setNotificationError({
            message: errorMessage,
            timeout: 5000,
          })
        );
      }
    }
  };

  if (isArrayHasItems(benefits)) {
    benefits.forEach((item) => {
      const { status, benefit, name, activeForms, forms, pdfBarcodes } = item;
      const benefitUUID = getIdFromIri(
        item["@id"],
        restUrls.employeeSubscriptionItems
      );

      if (!!activeForms.length || !!forms.length) {
        const { forms, activeForms } = item;
        if (
          forms.some(({ awaitsUpdate }) => awaitsUpdate) ||
          activeForms.some(({ awaitsUpdate }) => awaitsUpdate)
        ) {
          benefitsWithFormsToUpdate.push(item);
        }
      }

      if (!NOT_ACTIVE_STATUSES.includes(status)) {
        return;
      }

      const attachments = getBenefitAttachments(benefit);

      if (isArrayHasItems(attachments)) {
        attachments.forEach((attachment) => {
          if (attachment.type === "form") {
            const files = getAttachmentFiles(attachment);

            if (isArrayHasItems(files)) {
              files.forEach((file) => {
                if (file.language !== languageCode) return;
                const description = attachment.name;
                const fileUrl = `${restUrls.currentUserSubscription}/attachment/${file.id}`;
                elements.push(
                  <ActivityItem
                    key={`${benefitUUID}-attachment-${fileUrl}`}
                    onClick={() => downloadFile(fileUrl, name, true)}
                    name={name}
                    description={description}
                  >
                    {__("Pobierz")}
                  </ActivityItem>
                );
              });
            }
          }
        });
      }
      if (isArrayHasItems(activeForms)) {
        activeForms.forEach((activeForm) => {
          if (!activeForm.completed && activeForm.receiver !== "employee") {
            const description = `${__(
              "Nie został wypełniony formularz świadczeń abonamentowych, wysłany na adres e-mail:"
            )} ${activeForm.receiverEmail}`;
            const resendLink = `${
              restUrls.subscriptions
            }/${subscriptionId}/active-form-resend/${getIdFromIri(
              activeForm.formConfig,
              restUrls.activeForms
            )}/${activeForm.receiverEmail}`;

            elements.push(
              <EmailSenderActivityItem
                key={`${benefitUUID}-email-${resendLink}`}
                name={name}
                description={description}
                resendLink={resendLink}
                setData={setData}
                activeForm={activeForm}
                benefitUUID={benefitUUID}
              />
            );
          }
        });
      }
      if (pdfBarcodes.length) {
        const { manualActivationRequired } = benefit;
        pdfBarcodes.forEach(({ accepted, generateUrl, attachmentName }) => {
          if (
            accepted ||
            (!manualActivationRequired &&
              !activeForms.some(
                ({ receiver }) => receiver === FORM_RECEIVERS.OTHERS
              ))
          )
            return;

          elements.push(
            <ActivityItem
              key={`${benefitUUID}-email-${generateUrl}`}
              name={name}
              description={attachmentName}
              onClick={() => onGeneratePDFHandler(generateUrl)}
            >
              {__("Pobierz")}
            </ActivityItem>
          );
        });
      }
    });
    if (benefitsWithFormsToUpdate.length) {
      const description = __(`
        Uzupełnij swoje dane dotyczące abonamentów, 
        które wybrałeś oraz wpisz adres e-mail osób, 
        dla których wybrałeś abonament.
      `);
      elements.push(
        <FormFillerItem
          key="benefitsWithFormsToUpdate"
          benefitsWithFormsToUpdate={benefitsWithFormsToUpdate}
          totals={totals}
          name={__("Zmiana danych w formularzu - wymagane uzupełnienie")}
          description={description}
        />
      );
    }
  }

  if (showOfflineDeclarations) {
    elements.push(
      <FileDownloaderActivityItem
        key="selectedSubscriptions"
        name={__("Wybrane abonamenty")}
        file={`${
          restUrls.currentUserSubscriptionAgreements
        }${setBenefitGroupTypeParam(data.benefitGroupType)}`}
        description={__("Automatycznie generowane zgody i oświadczenia")}
      />
    );
  }

  return isArrayHasItems(elements) ? elements : [];
};
